import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import SectionTitle from '../../components/common/SectionTitle';
import Button from '../../components/ui/Button';
import { 
  fadeInUp, 
  fadeInLeft, 
  enhancedViewport, 
  successMessage, 
  errorMessage, 
  formReappear,
  formExit,
  messageExit
} from '../../utils/animation';
import './ContactForm.scss';

const ContactForm = () => {
  const { t } = useTranslation('contact');
  
  // OPTIMIZAT: Eliminat useAnimation() și useEffect() overhead - folosim whileInView direct
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Eliminăm eroarea pentru câmpul curent dacă utilizatorul tastează
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Funcție pentru validarea unui câmp la ieșirea din el (onBlur)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const fieldError = validateField(name, value);
    
    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
  };

  // Funcție de validare îmbunătățită
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) {
          return 'Numele este obligatoriu';
        }
        if (value.trim().length < 3) {
          return 'Numele trebuie să aibă cel puțin 3 caractere';
        }
        if (!/^[a-zA-ZăâîșțĂÂÎȘȚ\s]+$/.test(value.trim())) {
          return 'Numele poate conține doar litere și spații';
        }
        break;
        
      case 'email':
        if (!value.trim()) {
          return 'Email-ul este obligatoriu';
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value.trim())) {
          return 'Te rugăm să introduci o adresă de email validă (ex: nume@domeniu.com)';
        }
        break;
        
      case 'subject':
        if (!value.trim()) {
          return 'Subiectul este obligatoriu';
        }
        if (value.trim().length < 5) {
          return 'Subiectul trebuie să aibă cel puțin 5 caractere';
        }
        break;
        
      case 'message':
        if (!value.trim()) {
          return 'Mesajul este obligatoriu';
        }
        if (value.trim().length < 20) {
          return 'Mesajul trebuie să aibă cel puțin 20 de caractere';
        }
        if (value.trim().length > 1000) {
          return 'Mesajul nu poate avea mai mult de 1000 de caractere';
        }
        break;
        
      case 'phone':
        if (!value) {
          return 'Numărul de telefon este obligatoriu';
        }
        // Validare îmbunătățită pentru PhoneInput
        if (typeof value !== 'string') {
          return 'Te rugăm să introduci un număr de telefon valid';
        }
        // Verificăm lungimea minimă (fără prefix de țară)
        const cleanNumber = value.replace(/\D/g, ''); // Eliminăm toate caracterele non-digit
        if (cleanNumber.length < 8) {
          return 'Numărul de telefon trebuie să aibă cel puțin 8 cifre';
        }
        if (cleanNumber.length > 15) {
          return 'Numărul de telefon este prea lung (maxim 15 cifre)';
        }
        // Verificăm dacă numărul pare valid (nu doar cifre repetate)
        if (/^(\d)\1+$/.test(cleanNumber)) {
          return 'Te rugăm să introduci un număr de telefon valid';
        }
        break;
        
      default:
        break;
    }
    return null;
  };

  // Funcție de validare completă
  const validateForm = () => {
    const newErrors = {};
    
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    return newErrors;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validează formularul înainte de trimitere
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setIsSubmitting(true);
    setErrors({}); // Curățăm erorile anterioare
    
    // Trimitere date către server prin fetch
    fetch('/mail/mail.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'scr290!W@',
      },
      body: new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      }),
    })
    .then((response) => {
      // Verificăm dacă răspunsul are un status HTTP ok
      if (!response.ok) {
        return response.json().then(err => {
          throw new Error(err.error || 'Răspuns server invalid');
        });
      }
      return response.json();
    })
    .then((data) => {
      console.log('Mesaj trimis cu succes:', data);
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Resetăm formularul după trimitere cu succes
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      // Resetăm mesajul de succes după 4 secunde cu fade-out smooth
      setTimeout(() => {
        setIsSubmitted(false);
      }, 4000);
    })
    .catch((error) => {
      console.error('Eroare la trimiterea mesajului:', error);
      setIsSubmitting(false);
      
      // Setăm un mesaj de eroare în state pentru afișare în UI
      setErrors({
        ...errors,
        submit: error.message || "A apărut o eroare la trimiterea mesajului. Te rugăm să încerci din nou."
      });
    });
  };
  
  return (
    <section className="contact-section contact-section--main">
      <div className="container">
        <SectionTitle>{t('form.title')}</SectionTitle>
        <motion.p
          className="contact-form__intro"
          initial="hidden"
          whileInView="visible"
          viewport={enhancedViewport}
          variants={fadeInUp}
        >
          {t('form.intro')}
        </motion.p>
        
        <motion.div
          className="contact-form"
          initial="hidden"
          whileInView="visible"
          viewport={enhancedViewport}
          variants={fadeInLeft}
        >
          <AnimatePresence mode="wait">
            {isSubmitted && (
              <motion.div 
                className="contact-form__success"
                initial="hidden"
                animate="visible"
                exit={messageExit}
                variants={successMessage}
                key="success-message"
              >
                <FaCheck /> {t('form.success')}
              </motion.div>
            )}
          </AnimatePresence>
          
          <AnimatePresence mode="wait">
            {errors.submit && (
              <motion.div 
                className="contact-form__error-message"
                initial="hidden"
                animate="visible"
                exit={messageExit}
                variants={errorMessage}
                key="error-message"
              >
                <span>⚠️ {errors.submit}</span>
              </motion.div>
            )}
          </AnimatePresence>
          
          <AnimatePresence mode="wait">
            {!isSubmitted && (
              <motion.form 
                onSubmit={handleSubmit}
                initial="hidden"
                animate="visible"
                exit={formExit}
                variants={formReappear}
                key="contact-form"
              >
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label htmlFor="name">{t('form.fields.name.label')}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.name ? 'error' : ''}
                  required
                  disabled={isSubmitting}
                  placeholder={t('form.fields.name.placeholder')}
                />
                {errors.name && (
                  <span className="contact-form__error">{errors.name}</span>
                )}
              </div>
              
              <div className="contact-form__field">
                <label htmlFor="email">{t('form.fields.email.label')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.email ? 'error' : ''}
                  required
                  disabled={isSubmitting}
                  placeholder={t('form.fields.email.placeholder')}
                />
                {errors.email && (
                  <span className="contact-form__error">{errors.email}</span>
                )}
              </div>
            </div>
            
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label htmlFor="phone">{t('form.fields.phone.label')}</label>
                <PhoneInput
                  placeholder={t('form.fields.phone.placeholder')}
                  value={formData.phone}
                  onChange={(value) => {
                    setFormData(prev => ({
                      ...prev,
                      phone: value || ''
                    }));
                    // Eliminăm eroarea pentru câmpul curent dacă utilizatorul tastează
                    if (errors.phone && value) {
                      setErrors(prev => ({
                        ...prev,
                        phone: null
                      }));
                    }
                  }}
                  onBlur={() => {
                    const fieldError = validateField('phone', formData.phone);
                    setErrors(prev => ({
                      ...prev,
                      phone: fieldError
                    }));
                  }}
                  defaultCountry="RO"
                  countries={[
                    'RO', // România (default)
                    // Țări din jurul României
                    'HU', 'BG', 'RS', 'UA', 'MD', 'HR', 'SI', 'SK', 'CZ', 'PL',
                    // Europa de Vest
                    'DE', 'FR', 'IT', 'ES', 'PT', 'GB', 'NL', 'BE', 'AT', 'CH',
                    // Europa de Nord
                    'SE', 'NO', 'DK', 'FI',
                    // Alte țări europene importante
                    'GR', 'TR', 'IE', 'LU',
                    // Țări importante din afara Europei
                    'US', 'CA', 'AU'
                  ]}
                  international
                  className={errors.phone ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {errors.phone && (
                  <span className="contact-form__error">{errors.phone}</span>
                )}
              </div>
              
              <div className="contact-form__field">
                <label htmlFor="subject">{t('form.fields.company.label')}</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.subject ? 'error' : ''}
                  required
                  disabled={isSubmitting}
                  placeholder={t('form.fields.company.placeholder')}
                />
                {errors.subject && (
                  <span className="contact-form__error">{errors.subject}</span>
                )}
              </div>
            </div>
            
            <div className="contact-form__field">
              <label htmlFor="message">{t('form.fields.message.label')}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.message ? 'error' : ''}
                required
                disabled={isSubmitting}
                placeholder={t('form.fields.message.placeholder')}
              ></textarea>
              {errors.message && (
                <span className="contact-form__error">{errors.message}</span>
              )}
            </div>
            
            <div className="contact-form__submit">
              <Button 
                type="primary" 
                disabled={isSubmitting}
              >
                {isSubmitting ? t('form.sending') : t('form.button')}
              </Button>
            </div>
          </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
